import { useState, useRef, useEffect } from 'react';
import type { CustomerAction } from '@ghostwright/api-client';

interface FreeTextWithExampleProps {
  fieldLabel: string;
  exampleText: string;
  placeholder?: string;
  onAction: (action: CustomerAction) => void;
  disabled?: boolean;
}

export default function FreeTextWithExample({ fieldLabel, exampleText, placeholder, onAction, disabled }: FreeTextWithExampleProps) {
  const [text, setText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = '0';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  }, [text]);

  const handleSubmit = () => {
    if (text.trim().length === 0 || disabled) return;
    onAction({ kind: 'free-text-field', text: text.trim() });
    setText('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="mt-6">
      <label className="block text-sm font-medium text-foreground mb-1">
        {fieldLabel}
      </label>
      {exampleText && (
        <p className="text-sm italic text-muted-foreground mb-3">
          {exampleText}
        </p>
      )}

      <div className="relative flex items-end gap-2 border border-border rounded-card bg-background transition-[border-color,box-shadow] duration-80 ease-out focus-within:border-primary focus-within:shadow-[0_0_0_3px_hsl(var(--primary)/0.15)]">
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={placeholder ?? 'Be specific — a scene, a name, a moment.'}
          maxLength={2000}
          rows={1}
          className="flex-1 bg-transparent py-3 pl-4 pr-2 text-base leading-relaxed text-foreground resize-none min-h-[48px] max-h-[200px] focus:outline-none placeholder:text-placeholder disabled:opacity-50"
        />
        <button
          type="button"
          disabled={disabled || text.trim().length === 0}
          onClick={handleSubmit}
          className="shrink-0 m-1.5 inline-flex items-center gap-1.5 bg-primary text-primary-foreground text-sm font-medium py-2 px-4 rounded-button min-h-[36px] shadow-[inset_0_1px_0_hsl(var(--primary-foreground)/0.15)] transition-[opacity,transform] duration-80 ease-out hover:opacity-90 active:scale-[0.98] disabled:opacity-30 disabled:transform-none"
        >
          Send
          <span aria-hidden="true">&rarr;</span>
        </button>
      </div>

      <div className="flex items-center justify-between mt-3 px-1">
        <button
          type="button"
          disabled={disabled}
          onClick={() => onAction({ kind: 'park' })}
          className="text-xs text-muted-foreground inline-flex items-center gap-1 py-1 transition-colors duration-80 hover:text-foreground disabled:opacity-50"
        >
          Park this turn
        </button>
        <span className="text-xs text-placeholder">
          Shift+Enter for new line
        </span>
      </div>
    </div>
  );
}
